import { useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import tokens from "@/theme/harvest.json";
import { CeresMark } from "@/components/ceres/CeresMark";
import { API } from "@/lib/api";

const detailText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).filter(Boolean).join(" ") : "Something went wrong. Please try again.");

const Field = ({ id, label, children }) => (
  <label htmlFor={id} className="block">
    <span className="mb-1.5 block font-heading text-[11px] font-semibold uppercase tracking-[0.14em] text-hv-muted">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full border border-hv-rule bg-white px-3 py-3 text-[15px] text-hv-text transition-colors focus:border-hv-navy focus:outline-none focus:ring-2 focus:ring-hv-navy/15";

const BrandPanel = () => (
  <aside className="relative hidden flex-col justify-between overflow-hidden bg-hv-navy p-12 text-white lg:flex" data-testid="site-login-brand-panel">
    <img src={tokens.themes.etf.header_logo} alt="Harvest ETFs" className="h-8 w-auto self-start brightness-0 invert" />
    <div className="max-w-md">
      <CeresMark size={64} animate className="-ml-1 mb-8" />
      <h2 className="font-heading text-4xl font-bold leading-tight">Ceres</h2>
      <p className="mt-3 text-lg text-white/80">Harvest's fund guide. Answers about Harvest ETFs, straight from Harvest's own published documents, with sources.</p>
    </div>
    <p className="text-[12px] text-white/50">Harvest Portfolios Group Inc.</p>
    <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full border-[40px] border-white/5" aria-hidden="true" />
  </aside>
);

export const SiteLogin = ({ onSuccess }) => {
  const [f, setF] = useState({ username: "", password: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/site-login`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) });
      if (r.ok) return onSuccess();
      setError(detailText((await r.json().catch(() => ({}))).detail));
    } catch {
      setError("Can't reach Ceres right now. Check your connection and try again.");
    }
    setBusy(false);
  };
  return (
    <div className="grid min-h-screen bg-white font-body lg:grid-cols-[1.1fr_1fr]" data-testid="site-gate-locked">
      <BrandPanel />
      <main className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="site-login-form">
          <img src={tokens.themes.etf.header_logo} alt="Harvest ETFs" className="mb-10 h-8 w-auto" />
          <p className="flex items-center gap-2 font-heading text-[11px] font-semibold uppercase tracking-[0.16em] text-hv-orange"><Lock size={13} aria-hidden="true" /> Private preview</p>
          <h1 className="mt-2 font-heading text-3xl font-bold text-hv-navy">Sign in to Ceres</h1>
          <p className="mt-2 text-[14px] text-hv-muted">Enter the username and password you were given.</p>
          <div className="mt-8 space-y-5">
            <Field id="site-username" label="Username">
              <input id="site-username" autoComplete="username" required autoFocus value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className={inputCls} data-testid="site-login-username-input" />
            </Field>
            <Field id="site-password" label="Password">
              <div className="relative">
                <input id="site-password" type={show ? "text" : "password"} autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}
                  className={`${inputCls} pr-11`} data-testid="site-login-password-input" />
                <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} data-testid="site-login-toggle-password"
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-hv-muted transition-colors hover:text-hv-navy">
                  {show ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </Field>
          </div>
          {error && <p role="alert" className="mt-4 border-l-2 border-red-600 bg-red-50 px-3 py-2 text-[13px] text-red-800" data-testid="site-login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="site-login-submit-button"
            className="hv-btn mt-7 w-full border-hv-navy bg-hv-navy text-white hover:border-hv-mark hover:bg-hv-mark disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <p className="mt-6 text-[12px] text-hv-muted">You'll stay signed in on this device for 12 hours. Ceres gives general information only, not investment advice.</p>
        </form>
      </main>
    </div>
  );
};
